import { faker } from '@faker-js/faker';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
// @mui
import { useTheme } from '@mui/material/styles';
import { Grid, Container, Typography, Skeleton } from '@mui/material';

// components
import Page from '../components/Page';
// sections
import { AppNewsUpdate } from '../sections/dashboard/app';
import Map from '../sections/map/Map';
import { LineChart } from '../sections/dashboard/LineChart';
import { PieChart } from '../sections/dashboard/PieChart';
import { AnalyticsGQLFunction } from '../graphql/ANALYTICS';
import { Widget } from '../sections/dashboard/Widget';

// ----------------------------------------------------------------------
const DATARANGE = 30; // get data for the last 30 days
const NOW = new Date();

const YEAR = NOW.getUTCFullYear();
const MONTH = NOW.getUTCMonth();
const DAY = NOW.getUTCDate();
const numStartDate = Date.UTC(YEAR, MONTH, DAY - DATARANGE);
const numStartMonth = new Date(numStartDate);
const CHARTLABELS = createDateLabelArr({ DATARANGE, today: NOW });

// utils
function createDateLabelArr({ DATARANGE, today }) {
  const arr = [];
  for (let i = 0; i <= DATARANGE; i += 1) {
    today.setUTCDate(today.getUTCDate() - 1);
    arr.push(today.toString());
  }
  return arr;
}

function createMauDataArr({ CHARTLABELS, mauOfMonths }) {
  const pointer = 0;
  const lastMonth = new Date(CHARTLABELS[0]).getUTCMonth();
  const arr = [];
  for (let i = 0; i < CHARTLABELS.length; i += 1) {
    const offset = Math.abs(lastMonth - new Date(CHARTLABELS[i]).getUTCMonth());
    arr.push(mauOfMonths[pointer + offset]?.data ?? 0);
  }
  return arr;
}

export default function DashboardApp() {
  const { t } = useTranslation();
  const theme = useTheme();

  const { data: dashboardData, loading } = useQuery(AnalyticsGQLFunction.GET_DASHBOARD_DATA, {
    variables: {
      start: JSON.stringify(numStartDate),
      end: JSON.stringify(Date.UTC(YEAR, MONTH, DAY, 23, 59, 59)),
      startMonth: JSON.stringify(Date.UTC(numStartMonth.getUTCFullYear(), numStartMonth.getUTCMonth() - 1, 1)),
      endMonth: JSON.stringify(Date.UTC(YEAR, MONTH - 1, 1))
    }
  });

  const unauthorized = !dashboardData || typeof dashboardData === 'undefined';

  return (
    <Page title={t('nav.dashboard')}>
      <Container maxWidth="xl">
        <Typography variant="h4" sx={{ mb: 5 }}>
          {t('dashboard.title')}
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={12} md={12}>
            <Map />
          </Grid>
          {loading || unauthorized ? (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <Skeleton width={'100%'} height={250} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Skeleton width={'100%'} height={250} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Skeleton width={'100%'} height={250} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Skeleton width={'100%'} height={250} />
              </Grid>
              <Grid item xs={12} md={6} lg={8}>
                <Skeleton width={'100%'} height={300} />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <Skeleton width={'100%'} height={300} />
              </Grid>
              <Grid item xs={12} md={12} lg={12}>
                <Skeleton width={'100%'} height={300} />
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <Widget
                  title={t('dashboard.total_users')}
                  total={
                    dashboardData && dashboardData.totalUsersCount && dashboardData.totalUsersCount !== 'null'
                      ? JSON.parse(dashboardData.totalUsersCount)
                      : 0
                  }
                  color="info"
                  icon={'ic:round-people-alt'}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Widget
                  title={t('dashboard.current_dau')}
                  total={
                    dashboardData && dashboardData.latestDau && dashboardData.latestDau !== 'null'
                      ? JSON.parse(dashboardData.latestDau)
                      : 0
                  }
                  color="info"
                  icon={'ic:round-today'}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Widget
                  title={t('dashboard.current_mau')}
                  total={
                    dashboardData && dashboardData.latestMau && dashboardData.latestMau !== 'null'
                      ? JSON.parse(dashboardData.latestMau)
                      : 0
                  }
                  color="info"
                  icon={'ic:round-calendar-month'}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Widget
                  title={t('dashboard.messages_protected')}
                  total={
                    dashboardData && dashboardData.totalMessagesCount && dashboardData.totalMessagesCount !== 'null'
                      ? JSON.parse(dashboardData.totalMessagesCount)
                      : 0
                  }
                  color="info"
                  icon={'ic:round-message'}
                />
              </Grid>

              <Grid item xs={12} md={6} lg={8}>
                <LineChart
                  title={t('dashboard.active_users')}
                  // subheader="(+43%) than last YEAR"
                  chartLabels={CHARTLABELS}
                  chartData={[
                    {
                      name: t('dashboard.dau'),
                      type: 'column',
                      fill: 'solid',
                      data: dashboardData.dauOfDays.map((d) => JSON.stringify(d.data))
                    },
                    {
                      name: t('dashboard.mau_prev'),
                      type: 'area',
                      fill: 'gradient',
                      data: createMauDataArr({ CHARTLABELS, mauOfMonths: dashboardData.mauOfMonths })
                    }
                  ]}
                />
              </Grid>

              <Grid item xs={12} md={6} lg={4}>
                <PieChart
                  title={t('dashboard.active_timezones')}
                  subheader={t('dashboard.timezone_subtitle')}
                  // eslint-disable-next-line
                  chartData={dashboardData.timezonesOfUsers.map((data) => {
                    return { label: data.timezone, value: data.count };
                  })}
                  chartColors={[
                    theme.palette.primary.main,
                    theme.palette.chart.blue[0],
                    theme.palette.chart.violet[0],
                    theme.palette.chart.yellow[0]
                  ]}
                />
              </Grid>

              {0 ? (
                <Grid item xs={12} md={12} lg={12}>
                  <AppNewsUpdate
                    title="Push Notifications"
                    list={[...Array(5)].map((_, index) => ({
                      id: faker.datatype.uuid(),
                      title: faker.name.jobTitle(),
                      description: faker.name.jobTitle(),
                      image: `/static/mock-images/covers/cover_${index + 1}.jpg`,
                      postedAt: faker.date.recent()
                    }))}
                  />
                </Grid>
              ) : (
                <></>
              )}
            </>
          )}
        </Grid>
      </Container>
    </Page>
  );
}
